/* eslint-disable react/destructuring-assignment, react/no-unused-state, sort-keys */
import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';

import { debounce, throttle } from 'lodash';

import { Button, Dropdown, Icon, Input } from 'semantic-ui-react';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import { ASSIGNMENT_MENU_STATUS } from '../managers/AssignmentManager';
import { VIEW_SELECTION } from '../managers/NavigationManager';

import { setDocumentTitleFromSelectionView } from '../utils';

export default
@inject('assignmentManager', 'studentAssignmentManager')
@observer
class AssignmentMenu extends Component {
  debouncedSearch = debounce(async (menuStatus, search, sort) => {
    await this.resetSearch(menuStatus, search, sort);
    const { studentAssignmentManager } = this.props;
    if (studentAssignmentManager.loadingStudentAssignmentMenu) {
      studentAssignmentManager.setLoadingStudentAssignmentMenu(false);
    }
  }, 500);

  throttledSearch = throttle(async (menuStatus, search, sort) => {
    await this.resetSearch(menuStatus, search, sort);
    const { studentAssignmentManager } = this.props;
    if (studentAssignmentManager.loadingStudentAssignmentMenu) {
      studentAssignmentManager.setLoadingStudentAssignmentMenu(false);
    }
  }, 3000);

  constructor(props) {
    super(props);

    const { menuStatus, sort, search, t } = this.props;

    this.StudentAssignmentMenuStatusTabs = SatCoreComponent('StudentAssignmentMenuStatusTabs');

    this.state = {
      menuStatus: (menuStatus) || ASSIGNMENT_MENU_STATUS.CURRENT,
      sort: (sort) || 'asc',
      search: (search) || '',
      buttonIcon: (sort === 'desc') ? 'up arrow' : 'down arrow',
      buttonLabel: (sort === 'desc') ? 'Sort: Last' : 'Sort: First',
      titleName: t(ASSIGNMENT_MENU_STATUS.getTitle(
        (menuStatus) || ASSIGNMENT_MENU_STATUS.CURRENT
      ), 'Missing translation in AssignmentMenu')
    };
  }

  async componentDidMount() {
    const { studentAssignmentManager } = this.props;
    await this.resetSearch(this.state.menuStatus, this.state.search, this.state.sort);
    studentAssignmentManager.setCurrentStudentAssignmentMenuStatus(this.state.menuStatus);
  }

  componentDidUpdate() {
    const { t } = this.props;
    const { menuStatus } = this.state;
    const docSuffix = `docSuffix${ASSIGNMENT_MENU_STATUS.getTitle(menuStatus)}`;
    setDocumentTitleFromSelectionView(VIEW_SELECTION.ASSIGNMENTS, {
      VIEW_SELECTION,
      suffix: t(docSuffix)
    });
  }

  resetSearch = async (menuStatus, search, sort) => {
    await this.fetchSearch(menuStatus, search, sort);
  }

  fetchSearch = async (menuStatus, search, sort) => {
    await this.props.fetchAssignments(menuStatus, search, sort);
  }

  handleChangeSearch = async (_event, { value }) => {
    if (value.length > 2 || value.length === 0) {
      const { state } = this;
      this.debouncedSearch(state.menuStatus, value, state.sort);
    }
    this.setState({ search: value });
  }

  handleChangeStatusMenu = async (_event, { name }) => {
    const { assignmentManager, studentAssignmentManager, t } = this.props;

    if (studentAssignmentManager.loadingStudentAssignmentMenu) {
      return;
    }
    const { state } = this;

    studentAssignmentManager.setLoadingStudentAssignmentMenu(true);
    assignmentManager.clearAssignments();

    const title = t(ASSIGNMENT_MENU_STATUS.getTitle(name),
      'Missing translation in AssignmentMenu'
    );
    this.setState({ menuStatus: name, titleName: title });
    studentAssignmentManager.setCurrentStudentAssignmentMenuStatus(name);

    this.throttledSearch(name, state.search, state.sort);
  }

  handleChangeSort = async () => {
    const { sort, menuStatus, search } = this.state;
    if (sort === 'asc') {
      this.setState({ sort: 'desc', buttonIcon: 'up arrow', buttonLabel: 'Sort: Last' });
      await this.resetSearch(menuStatus, search, 'desc');
    } else {
      this.setState({ sort: 'asc', buttonIcon: 'down arrow', buttonLabel: 'Sort: First' });
      await this.resetSearch(menuStatus, search, 'asc');
    }
  }

  renderStudentAssignmentMenuStatusTabs = () => {
    const { studentAssignmentManager } = this.props;
    const { loadingStudentAssignmentMenu } = studentAssignmentManager;
    const { menuStatus } = this.state;
    const { StudentAssignmentMenuStatusTabs } = this;
    return (
      <StudentAssignmentMenuStatusTabs
        activeMenuStatus={menuStatus}
        idPrefix='assignment-menu'
        loading={loadingStudentAssignmentMenu}
        loadingClassName='loading-student-assignment-menu'
        onClick={this.handleChangeStatusMenu}
        tabPanelId='assignment-menu-panel' />
    );
  }

  renderStudentAssignmentMenuSearch = () => {
    const { t } = this.props;
    const { search } = this.state;
    return (
      <Input
        aria-label={t('searchContentAriaLabel')}
        className='search-input student-assignment-search-input'
        icon='search'
        label={{ content: t('searchContentLabel'), basic: true }}
        labelPosition='left'
        name='search'
        onChange={this.handleChangeSearch}
        placeholder={t('SearchPlaceholder', 'No placeholder translation')}
        type='text'
        value={search} />
    );
  }

  // Returns null when allowStudentAssignmentResourceTypeFilter is off.
  renderStudentAssignmentMenuResourceFilter = () => {
    const { assignmentResourceFilter, handleChangeAssignmentResourceFilter, studentAssignmentManager } = this.props;
    if (!studentAssignmentManager.allowStudentAssignmentResourceTypeFilter) {
      return null;
    }
    return (
      <div className='student-assignment-menu-resource-filter sa-assignment-filter'>
        <Dropdown
          fluid
          onChange={handleChangeAssignmentResourceFilter}
          options={[
            { selected: true, text: 'All Assignments', value: 'all-assignments' },
            { text: 'Assessments Only', value: 'assessments-only' }
          ]}
          selection
          value={assignmentResourceFilter} />
      </div>
    );
  }

  /**
   * Visually hidden status heading; for accessibility only.
   * Provides heading hierarchy for screen readers.
   */
  renderStudentAssignmentMenuStatusHeading = () => {
    const { t } = this.props;
    const { menuStatus } = this.state;
    const ariaLabelMenuStatus = menuStatus ? t(`aria${ASSIGNMENT_MENU_STATUS.getTitle(menuStatus)}`) : '';
    return (
      <h2
        aria-label={ariaLabelMenuStatus}
        className='student-assignment-menu-status-heading sr-only sa-second-header-label aria-offscreen-relative'>
        {ariaLabelMenuStatus}
      </h2>
    );
  }

  renderStudentAssignmentMenuSortButton = () => {
    const { buttonIcon, buttonLabel } = this.state;
    return (
      <Button icon labelPosition='right' onClick={this.handleChangeSort}>
        {buttonLabel}
        <Icon name={buttonIcon} />
      </Button>
    );
  }

  render() {
    return (
      <>
        {this.renderStudentAssignmentMenuStatusHeading()}

        <div className='student-assignment-menu-row sa-header-row'>
          {this.renderStudentAssignmentMenuStatusTabs()}
          {this.renderStudentAssignmentMenuResourceFilter()}
          {this.renderStudentAssignmentMenuSearch()}
        </div>

        {/* TODO DEMO-3099: sa-second-header-row no longer in scope; sort button removed for the redesign */}
        {/* <div className='sa-second-header-row'>
          <div className='sa-search-n-sort'>
            {this.renderStudentAssignmentMenuSortButton()}
          </div>
        </div> */}
      </>
    );
  }
}

SatCoreRegister('AssignmentMenu', AssignmentMenu);
